import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Form, FormGroup, Input, Label, Spinner, FormFeedback, Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import { media } from '../data/cloudinary';
import { routeUrls } from '../config/routes';
import { resetPassword } from '../redux/actions';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const ResetPassword = ({ showLogo, onLogoClick, resetId }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const loader = useSelector(state => state.loader);
  const [browniteInfo, setBrowniteInfo] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validatePassword = (password) => {
    const validationErrors = [];
    if (password.length < 6) {
      validationErrors.push('Password must be at least 6 characters');
    }
    if (!/[A-Z]/.test(password)) {
      validationErrors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      validationErrors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      validationErrors.push('Password must contain at least one number');
    }
    return validationErrors;
  };

  const handleInput = e => {
    const { name, value } = e.target;
    setBrowniteInfo(prevState => ({
      ...prevState,
      [name]: value,
    }));

    // Real-time validation
    const newErrors = { ...errors };
    if (name === 'newPassword') {
      const passwordErrors = validatePassword(value);
      newErrors.newPassword = passwordErrors.length > 0 ? passwordErrors : null;
      // Also check confirm password match if it's been entered
      if (browniteInfo.confirmPassword && value !== browniteInfo.confirmPassword) {
        newErrors.confirmPassword = ['Passwords do not match'];
      } else if (browniteInfo.confirmPassword) {
        newErrors.confirmPassword = null;
      }
    }
    if (name === 'confirmPassword') {
      if (value !== browniteInfo.newPassword) {
        newErrors.confirmPassword = ['Passwords do not match'];
      } else {
        newErrors.confirmPassword = null;
      }
    }
    setErrors(newErrors);
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const isFormValid = () => {
    const passwordErrors = validatePassword(browniteInfo.newPassword);
    const passwordsMatch = browniteInfo.newPassword === browniteInfo.confirmPassword;
    return passwordErrors.length === 0 && passwordsMatch && browniteInfo.newPassword.length > 0;
  };

  const handleSubmit = event => {
    event.preventDefault();

    // Mark all fields as touched
    setTouched({ newPassword: true, confirmPassword: true });

    // Validate before submit
    const passwordErrors = validatePassword(browniteInfo.newPassword);
    const newErrors = {};

    if (passwordErrors.length > 0) {
      newErrors.newPassword = passwordErrors;
    }
    if (browniteInfo.newPassword !== browniteInfo.confirmPassword) {
      newErrors.confirmPassword = ['Passwords do not match'];
    }
    if (!browniteInfo.confirmPassword) {
      newErrors.confirmPassword = ['Please confirm your password'];
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      dispatch(resetPassword({ newPassword: browniteInfo.newPassword, _id: resetId }));
    }
  };

  const getPasswordStrength = (password) => {
    if (password.length === 0) return null;
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { text: 'Weak', color: 'danger' };
    if (strength <= 4) return { text: 'Medium', color: 'warning' };
    return { text: 'Strong', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(browniteInfo.newPassword);

  return (
    <Form onSubmit={handleSubmit}>
      {showLogo && (
        <div className="text-center pb-4">
          <img
            src={media.logo}
            className="rounded"
            style={{ width: 60, height: 60, cursor: 'pointer' }}
            alt="logo"
            onClick={onLogoClick}
          />
        </div>
      )}

      <FormGroup>
        <Label for="newPassword">New Password</Label>
        <div className="input-group">
          <Input
            type={showPassword ? "text" : "password"}
            id="newPassword"
            value={browniteInfo.newPassword}
            onChange={handleInput}
            onBlur={handleBlur}
            name="newPassword"
            placeholder="Enter your new password"
            invalid={touched.newPassword && errors.newPassword?.length > 0}
            valid={touched.newPassword && !errors.newPassword && browniteInfo.newPassword.length > 0}
            className="form-control"
          />
          <div className="input-group-append">
            <span
              className="input-group-text"
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer' }}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </span>
          </div>
          {touched.newPassword && errors.newPassword && (
            <FormFeedback>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                {errors.newPassword.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </FormFeedback>
          )}
        </div>
        {passwordStrength && (
          <small className={`text-${passwordStrength.color}`}>
            Password strength: {passwordStrength.text}
          </small>
        )}
      </FormGroup>

      <FormGroup>
        <Label for="confirmPassword">Confirm Password</Label>
        <div className="input-group">
          <Input
            type={showConfirmPassword ? "text" : "password"}
            id="confirmPassword"
            value={browniteInfo.confirmPassword}
            onChange={handleInput}
            onBlur={handleBlur}
            name="confirmPassword"
            placeholder="Confirm your new password"
            invalid={touched.confirmPassword && errors.confirmPassword?.length > 0}
            valid={touched.confirmPassword && !errors.confirmPassword && browniteInfo.confirmPassword.length > 0}
            className="form-control"
          />
          <div className="input-group-append">
            <span
              className="input-group-text"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{ cursor: 'pointer' }}
            >
              {showConfirmPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </span>
          </div>
          {touched.confirmPassword && errors.confirmPassword && (
            <FormFeedback style={{ display: 'block' }}>
              {errors.confirmPassword[0]}
            </FormFeedback>
          )}
        </div>
      </FormGroup>

      <Alert color="info" className="py-2" style={{ fontSize: '0.85rem' }}>
        <strong>Password requirements:</strong>
        <ul className="mb-0 ps-3">
          <li>At least 6 characters</li>
          <li>One uppercase letter (A-Z)</li>
          <li>One lowercase letter (a-z)</li>
          <li>One number (0-9)</li>
        </ul>
      </Alert>

      <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size="lg"
          className="bg-gradient-theme-left border-0"
          block
          onClick={handleSubmit}
          disabled={!isFormValid() || (loader && loader.resetPassword)}
        >
          Reset Password{' '}
          {loader && loader.resetPassword && (
            <Spinner color="light" size="sm" />
          )}
        </Button>
      </FormGroup>
      <div className="text-center pt-1">
        <h6>or</h6>
        <h6>
          <p onClick={() => history.push(routeUrls.login)} style={{ cursor: 'pointer', color: '#13287D' }}>
            Back to Login
          </p>
        </h6>
      </div>
    </Form>
  );
};

ResetPassword.propTypes = {
  showLogo: PropTypes.bool,
  onLogoClick: PropTypes.func,
  resetId: PropTypes.string,
};

ResetPassword.defaultProps = {
  showLogo: true,
  onLogoClick: () => { },
};
export default ResetPassword;
